import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useLogout } from '../../../hooks';
import toast from 'react-hot-toast';
import Sidebar from '../../components/Sidebar/Sidebar';
import AircraftManagement from '../../components/AircraftManagement/AircraftManagement';
import AircraftDetails from '../../components/AircraftDetails/AircraftDetails';
import FlightManagement from '../../components/FlightManagement/FlightManagement';
import AddAircraft from '../AddAircraft/AddAircraft';
import FlightFormPage from '../FlightForm/FlightFormPage';
import FlightDetailsPage from '../FlightDetails/FlightDetailsPage';
import AccountSettings from '../../components/AccountSettings/AccountSettings';
import Messages from '../../components/Messages/Messages';
import DashboardHeader from '../../components/DashboardHeader/DashboardHeader';
import RevenuePage from '../Revenue/RevenuePage';
import { fetchUnreadCount } from '../../../store/slices/notificationSlice';
import PendingBookingsPage from '../PendingBookings/PendingBookingsPage';

const AircraftDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { logout } = useLogout();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  // Get unread notification count from Redux
  const { unreadCount } = useSelector((state) => state.notifications);

  // Fetch unread count on mount
  useEffect(() => {
    dispatch(fetchUnreadCount());
  }, [dispatch]);

  // Fetch pending booking count for badge
  useEffect(() => {
    const load = async () => {
      try {
        const { default: flightManagementService } = await import('../../../services/flightManagementService');
        const res = await flightManagementService.getPendingConfirmationBookings();
        const active = (res.data || []).filter(
          (b) => b.pjo_decision_deadline && new Date(b.pjo_decision_deadline) > new Date()
        );
        setPendingCount(active.length);
      } catch (_) {}
    };
    load();
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, []);

  const menuItems = [
    {
      id: 'aircraft-management',
      label: 'Aircraft Management',
      icon: 'fas fa-plane-departure',
      path: '/airliner/aircraft'
    },
    {
      id: 'flight-management',
      label: 'Flight Management',
      icon: 'icon-flight',
      path: '/airliner/flights'
    },
    {
      id: 'pending-bookings',
      label: 'Pending Bookings',
      icon: 'fas fa-clock',
      badge: pendingCount > 0 ? pendingCount : undefined,
      path: '/airliner/pending-bookings'
    },
    {
      id: 'revenue-management',
      label: 'Revenue',
      icon: 'fas fa-chart-line',
      path: '/airliner/revenue'
    },
    {
      id: 'account-settings',
      label: 'Account Settings',
      icon: 'fas fa-gears',
      path: '/airliner/profile'
    },
    {
      id: 'messages',
      label: 'Messages',
      icon: 'fas fa-bell',
      badge: unreadCount > 99 ? '99+' : (unreadCount || 0),
      path: '/airliner/notification'
    },
    {
      id: 'logout',
      label: 'Logout',
      icon: 'icon-logout'
    }
  ];

  const handleMenuItemClick = async (itemId) => {
    if (itemId === 'logout') {
      const success = await logout({
        showConfirmation: true,
        redirectTo: '/',
        onSuccess: () => {
          toast.success('Logged out successfully');
        },
        onError: (error) => {
          toast.error('Logout failed. Please try again.');
        }
      });
      return;
    }

    const menuItem = menuItems.find(item => item.id === itemId);
    if (menuItem && menuItem.path) {
      navigate(menuItem.path);
    }
  };

  // Determine active item based on current path
  const getActiveItem = () => {
    const path = location.pathname;
    if (path.includes('/aircraft')) return 'aircraft-management';
    if (path.includes('/flights')) return 'flight-management';
    if (path.includes('/pending-bookings')) return 'pending-bookings';
    if (path.includes('/revenue')) return 'revenue-management';
    if (path.includes('/profile')) return 'account-settings';
    if (path.includes('/notification')) return 'messages';
    return 'aircraft-management';
  };

  return (
    <div className="flex min-h-screen bg-jet-gray-100 w-full">
      <DashboardHeader onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)} />
      <Sidebar
        menuItems={menuItems}
        activeItem={getActiveItem()}
        onMenuItemClick={handleMenuItemClick}
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
      />
      <div className="flex-1 min-w-0 pt-12 bg-jet-gray-100 min-h-screen box-border md:ml-44 ml-0">
        <Routes>
          <Route path="/" element={<Navigate to="/airliner/profile" replace />} />
          <Route path="/aircraft" element={<AircraftManagement />} />
          <Route path="/aircraft/:id" element={<AircraftDetails />} />
          <Route path="/edit-aircraft/:id" element={<AddAircraft />} />
          <Route path="/flights" element={<FlightManagement />} />
          <Route path="/flight/:id" element={<FlightDetailsPage />} />
          <Route path="/add-aircraft" element={<AddAircraft />} />
          <Route path="/add-flight" element={<FlightFormPage />} />
          <Route path="/aircraft/:aircraftId/add-flight" element={<FlightFormPage />} />
          <Route path="/edit-flight/:flightId" element={<FlightFormPage />} />
          <Route path="/profile" element={<AccountSettings />} />
          <Route path="/notification" element={<Messages />} />
          <Route path="/pending-bookings" element={<PendingBookingsPage />} />
          <Route path="/revenue" element={<RevenuePage />} />
        </Routes>
      </div>
    </div>
  );
};

export default AircraftDashboard;
